/**
 * useAdminListings Hook
 *
 * Provides admin listing management with filtering and pagination
 * using real Firestore queries.
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export interface AdminListing {
  id: string;
  title: string;
  slug: string;
  propertyType: string;
  status: 'active' | 'paused' | 'draft' | 'pending' | 'deleted';
  featured: boolean;
  hostId: string;
  hostName: string;
  hostEmail: string;
  location: {
    city: string;
    region: string;
    country: string;
  };
  pricing: {
    nightlyRate: number;
    currency: string;
  };
  photos: { url: string; caption?: string }[];
  bookingsCount: number;
  viewsCount: number;
  avgRating?: number;
  reviewsCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface AdminListingsFilter {
  search?: string;
  status?: 'active' | 'paused' | 'draft' | 'pending' | '';
  propertyType?: string;
  location?: string;
  isFeatured?: boolean | '';
  sortBy?: 'createdAt' | 'title' | 'bookingsCount' | 'nightlyRate';
  sortOrder?: 'asc' | 'desc';
}

export interface ListingsStats {
  total: number;
  active: number;
  pending: number;
  paused: number;
  featured: number;
}

const PAGE_SIZE = 10;

const toDate = (val: any): Date => {
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return val ? new Date(val) : new Date();
};

// Map 'published' status to 'active' for display
const mapStatus = (status: string): AdminListing['status'] => {
  if (status === 'published') return 'active';
  if (['active', 'paused', 'draft', 'pending', 'deleted'].includes(status))
    return status as AdminListing['status'];
  return 'draft';
};

export const useAdminListings = (filter: AdminListingsFilter, page: number = 0) => {
  const [listings, setListings] = useState<AdminListing[]>([]);
  const [stats, setStats] = useState<ListingsStats>({ total: 0, active: 0, pending: 0, paused: 0, featured: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchListings = async () => {
      setIsLoading(true);
      try {
        const q = query(collection(db, 'listings'), orderBy('createdAt', 'desc'));
        const [listingsSnap, usersSnap] = await Promise.all([
          getDocs(q),
          getDocs(collection(db, 'users')),
        ]);

        // Build user lookup
        const userMap = new Map<string, { name: string; email: string }>();
        usersSnap.docs.forEach((d) => {
          const data = d.data();
          userMap.set(d.id, {
            name: data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            email: data.email || '',
          });
        });

        let allListings: AdminListing[] = listingsSnap.docs.map((docSnap) => {
          const data = docSnap.data();
          const host = userMap.get(data.hostId) || { name: 'Unknown', email: '' };
          return {
            id: docSnap.id,
            title: data.title || 'Untitled',
            slug: data.slug || docSnap.id,
            propertyType: data.propertyType || 'other',
            status: mapStatus(data.status),
            featured: data.isFeatured || false,
            hostId: data.hostId || '',
            hostName: host.name,
            hostEmail: host.email,
            location: {
              city: data.location?.city || '',
              region: data.location?.state || data.location?.area || '',
              country: data.location?.country || 'Lebanon',
            },
            pricing: {
              nightlyRate: data.pricing?.nightlyRate || 0,
              currency: data.pricing?.currency || 'USD',
            },
            photos: (data.photos || []).slice(0, 3),
            bookingsCount: data.bookingsCount || 0,
            viewsCount: data.viewsCount || 0,
            avgRating: data.avgRating,
            reviewsCount: data.reviewsCount || 0,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
          };
        });

        // Calculate stats from full dataset
        const listingsStats: ListingsStats = {
          total: allListings.length,
          active: allListings.filter((l) => l.status === 'active').length,
          pending: allListings.filter((l) => l.status === 'pending').length,
          paused: allListings.filter((l) => l.status === 'paused').length,
          featured: allListings.filter((l) => l.featured).length,
        };

        // Apply filters
        if (filter.search) {
          const searchLower = filter.search.toLowerCase();
          allListings = allListings.filter(
            (l) =>
              l.title.toLowerCase().includes(searchLower) ||
              l.hostName.toLowerCase().includes(searchLower) ||
              l.location.city.toLowerCase().includes(searchLower)
          );
        }

        if (filter.status) {
          const statusFilter = filter.status === 'active' ? 'active' : filter.status;
          allListings = allListings.filter((l) => l.status === statusFilter);
        }

        if (filter.propertyType) {
          allListings = allListings.filter((l) => l.propertyType === filter.propertyType);
        }

        if (filter.location) {
          allListings = allListings.filter(
            (l) => l.location.city === filter.location || l.location.region === filter.location
          );
        }

        if (filter.isFeatured === true) {
          allListings = allListings.filter((l) => l.featured);
        } else if (filter.isFeatured === false) {
          allListings = allListings.filter((l) => !l.featured);
        }

        // Sort
        const sortBy = filter.sortBy || 'createdAt';
        const sortOrder = filter.sortOrder || 'desc';
        allListings.sort((a, b) => {
          let aVal: number | string;
          let bVal: number | string;
          if (sortBy === 'title') { aVal = a.title.toLowerCase(); bVal = b.title.toLowerCase(); }
          else if (sortBy === 'bookingsCount') { aVal = a.bookingsCount; bVal = b.bookingsCount; }
          else if (sortBy === 'nightlyRate') { aVal = a.pricing.nightlyRate; bVal = b.pricing.nightlyRate; }
          else { aVal = a.createdAt.getTime(); bVal = b.createdAt.getTime(); }
          return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
        });

        if (!cancelled) {
          setStats(listingsStats);
          setTotalCount(allListings.length);
          const start = page * PAGE_SIZE;
          setListings(allListings.slice(start, start + PAGE_SIZE));
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch listings:', err);
        if (!cancelled) {
          setListings([]);
          setTotalCount(0);
          setIsLoading(false);
        }
      }
    };

    fetchListings();
    return () => { cancelled = true; };
  }, [filter.search, filter.status, filter.propertyType, filter.location, filter.isFeatured, filter.sortBy, filter.sortOrder, page]);

  return {
    listings,
    stats,
    totalCount,
    isLoading,
    hasMore: (page + 1) * PAGE_SIZE < totalCount,
    totalPages: Math.ceil(totalCount / PAGE_SIZE),
  };
};

export const useAdminListing = (listingId: string) => {
  const [listing, setListing] = useState<AdminListing | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchListing = async () => {
      setIsLoading(true);
      try {
        const docSnap = await getDoc(doc(db, 'listings', listingId));
        if (!cancelled && docSnap.exists()) {
          const data = docSnap.data();
          // Fetch host info
          let hostName = 'Unknown';
          let hostEmail = '';
          if (data.hostId) {
            const hostSnap = await getDoc(doc(db, 'users', data.hostId));
            if (hostSnap.exists()) {
              const hostData = hostSnap.data();
              hostName = hostData.displayName || `${hostData.firstName || ''} ${hostData.lastName || ''}`.trim();
              hostEmail = hostData.email || '';
            }
          }

          setListing({
            id: docSnap.id,
            title: data.title || 'Untitled',
            slug: data.slug || docSnap.id,
            propertyType: data.propertyType || 'other',
            status: mapStatus(data.status),
            featured: data.isFeatured || false,
            hostId: data.hostId || '',
            hostName,
            hostEmail,
            location: {
              city: data.location?.city || '',
              region: data.location?.state || data.location?.area || '',
              country: data.location?.country || 'Lebanon',
            },
            pricing: {
              nightlyRate: data.pricing?.nightlyRate || 0,
              currency: data.pricing?.currency || 'USD',
            },
            photos: data.photos || [],
            bookingsCount: data.bookingsCount || 0,
            viewsCount: data.viewsCount || 0,
            avgRating: data.avgRating,
            reviewsCount: data.reviewsCount || 0,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
          });
        } else if (!cancelled) {
          setListing(null);
        }
        if (!cancelled) setIsLoading(false);
      } catch (err) {
        console.error('Failed to fetch listing:', err);
        if (!cancelled) { setListing(null); setIsLoading(false); }
      }
    };

    if (listingId) fetchListing();
    return () => { cancelled = true; };
  }, [listingId]);

  return { listing, isLoading };
};

export default useAdminListings;
